import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/components/utils";

const badgeVariants = cva(
  "relative inline-flex items-center font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        success: "bg-green-600 text-white",
        error: "bg-destructive text-destructive-foreground",
        info: "bg-blue-600 text-white",
        warning: "bg-orange-600 text-white",
        light: "bg-slate-100 text-subline",
        disabled: "bg-slate-100 text-subline",
      },
      outline: {
        none: "border-transparent",
        bordered: "border",
      },
      type: {
        default: "cursor-default",
        button: "cursor-pointer",
      },
      size: {
        default: "px-2 py-1 text-xs",
        sm: "px-2.5 py-1 text-sm",
        md: "px-3 py-2 text-base",
        lg: "px-3 py-2 text-lg",
      },
      shape: {
        default: "rounded-sm",
        square: "rounded-none",
        circle: "rounded-full",
      },
    },
    compoundVariants: [
      {
        outline: "bordered",
        variant: "default",
        class: "bg-card border-primary text-primary",
      },
      {
        outline: "bordered",
        variant: "secondary",
        class: "bg-card border-secondary text-secondary",
      },
      {
        outline: "bordered",
        variant: "success",
        class: "bg-card border-green-600 text-green-600",
      },
      {
        outline: "bordered",
        variant: "error",
        class: "bg-card border-destructive text-destructive",
      },
      {
        outline: "bordered",
        variant: "info",
        class: "bg-card border-blue-600 text-blue-600",
      },
      {
        outline: "bordered",
        variant: "warning",
        class: "bg-card border-orange-600 text-orange-600",
      },
      {
        outline: "bordered",
        variant: "disabled",
        class: "bg-card border-slate-200 text-slate-400",
      },
      {
        type: "button",
        variant: "default",
        outline: "none",
        class: "hover:bg-primary/90",
      },
      {
        type: "button",
        variant: "secondary",
        outline: "none",
        class: "hover:bg-secondary/90",
      },
      {
        type: "button",
        variant: "success",
        outline: "none",
        class: "hover:bg-green-500",
      },
      {
        type: "button",
        variant: "error",
        outline: "none",
        class: "hover:bg-destructive/90",
      },
      {
        type: "button",
        variant: "info",
        outline: "none",
        class: "hover:bg-blue-500",
      },
      {
        type: "button",
        variant: "warning",
        outline: "none",
        class: "hover:bg-orange-500",
      },
      {
        type: "button",
        variant: "disabled",
        class: "cursor-not-allowed",
      },
    ],
    defaultVariants: {
      variant: "default",
      outline: "none",
      type: "default",
      size: "default",
      shape: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({
  className,
  variant,
  outline,
  type,
  size,
  shape,
  ...props
}: BadgeProps) {
  return (
    <div
      className={cn(
        badgeVariants({ variant, outline, type, size, shape }),
        className
      )}
      {...props}
    />
  );
}

export default Badge;
